import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import AdminNav from '@/components/AdminNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCodes = () => {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_codes: 0, active_codes: 0 });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    reward_type: 'DISCOUNT_50_YEARLY',
    discount_percent: 50,
    usage_limit: 1,
    active: true,
    expires_at: ''
  });

  const fetchCodes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      const response = await axios.get(`${API}/codes/admin/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setCodes(response.data.codes);
        setStats({
          total_codes: response.data.total_codes,
          active_codes: response.data.active_codes
        });
      }
    } catch (error) {
      console.error('Fetch codes error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load codes');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/codes/admin/create`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Code created successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create code');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/codes/admin/update/${editingCode}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Code updated successfully!');
      setEditingCode(null);
      resetForm();
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update code');
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete code ${code}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/codes/admin/delete/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Code deleted successfully!');
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete code');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/codes/admin/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `claim_codes_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Codes exported successfully!');
    } catch (error) {
      toast.error('Failed to export codes');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      reward_type: 'DISCOUNT_50_YEARLY',
      discount_percent: 50,
      usage_limit: 1,
      active: true,
      expires_at: ''
    });
  };

  const openEditModal = (code) => {
    setEditingCode(code.code);
    setFormData({
      code: code.code,
      reward_type: code.reward_type,
      discount_percent: code.discount_percent,
      usage_limit: code.usage_limit,
      active: code.active,
      expires_at: code.expires_at || ''
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Loading codes...</p>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f9fafb', 
      padding: '40px 20px' 
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '30px',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '10px 20px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                marginBottom: '16px'
              }}
            >
              ← Back to Home
            </button>
            <h1 style={{ fontSize: '2.5rem', marginBottom: '8px', color: '#1f2937' }}>
              🎟️ Promo Codes Dashboard
            </h1>
            <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>
              Manage discount codes and track redemptions
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                padding: '12px 24px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(37,99,235,0.3)'
              }}
            >
              + Create Code
            </button>
            <button
              onClick={handleExport}
              style={{
                padding: '12px 24px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(16,185,129,0.3)'
              }}
            >
              📥 Export CSV
            </button>
          </div>
        </div>

        {/* Admin Navigation */}
        <AdminNav />

        {/* Stats */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '8px' }}>Total Codes</p>
            <p style={{ fontSize: '2.5rem', fontWeight: '700', color: '#2563eb' }}>
              {stats.total_codes}
            </p>
          </div>

          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '8px' }}>Active Codes</p>
            <p style={{ fontSize: '2.5rem', fontWeight: '700', color: '#10b981' }}>
              {stats.active_codes}
            </p>
          </div>

          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '8px' }}>Total Redemptions</p>
            <p style={{ fontSize: '2.5rem', fontWeight: '700', color: '#f59e0b' }}>
              {codes.reduce((sum, c) => sum + (c.used_count || 0), 0)}
            </p>
          </div>
        </div>

        {/* Codes Table */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Code</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Discount</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Usage</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Status</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Expires</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                      No codes yet. Create your first code!
                    </td>
                  </tr>
                ) : (
                  codes.map((code) => (
                    <tr key={code.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '16px', fontWeight: '600', fontFamily: 'monospace', color: '#1f2937' }}>
                        {code.code}
                      </td>
                      <td style={{ padding: '16px', color: '#10b981', fontWeight: '600' }}>
                        {code.discount_percent}% OFF
                      </td>
                      <td style={{ padding: '16px', color: '#6b7280' }}>
                        {code.used_count}/{code.usage_limit}
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          background: code.active ? '#d1fae5' : '#fee2e2',
                          color: code.active ? '#065f46' : '#991b1b'
                        }}>
                          {code.active ? '✓ Active' : '✗ Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '16px', color: '#6b7280', fontSize: '0.9rem' }}>
                        {code.expires_at ? new Date(code.expires_at).toLocaleDateString() : 'No expiry'}
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => openEditModal(code)}
                            style={{
                              padding: '6px 12px',
                              background: '#2563eb',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer'
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(code.code)}
                            style={{
                              padding: '6px 12px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingCode) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}
        onClick={() => {
          setShowCreateModal(false);
          setEditingCode(null);
          resetForm();
        }}
        >
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontSize: '1.5rem', marginBottom: '20px' }}>
              {editingCode ? 'Edit Code' : 'Create New Code'}
            </h3>

            <form onSubmit={editingCode ? handleUpdate : handleCreate}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Code *</label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  disabled={!!editingCode}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Discount % *</label>
                <input
                  type="number"
                  required
                  min="1"
                  max="100"
                  value={formData.discount_percent}
                  onChange={(e) => setFormData({...formData, discount_percent: parseInt(e.target.value)})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Usage Limit *</label>
                <input
                  type="number"
                  required
                  min="1"
                  value={formData.usage_limit}
                  onChange={(e) => setFormData({...formData, usage_limit: parseInt(e.target.value)})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({...formData, active: e.target.checked})}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span style={{ fontWeight: '600' }}>Active</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {editingCode ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingCode(null);
                    resetForm();
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCodes;
